"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
import { Mail } from "lucide-react";
const clauses = [{
  title: "1. Ownership & Liability",
  content: "You retain ownership of all accounts, platforms, and assets related to your business. You are solely responsible for all content, information, and materials published — even if designed, created, or suggested by Website Shop. Website Shop is not liable for any damages, losses, or misuse of platforms or assets by any person, authorised or unauthorised.\n\nFor the first twelve (12) months following the website’s completion (“Management Period”), your website will be hosted and managed under Website Shop’s professional dashboard to ensure security, updates, and performance. During this time, administrative access will remain with Website Shop.\n\nAt the end of the Management Period, you can continue to have Website Shop retain administrative access for an additional fee which will be quoted upon renewal. Alternatively, you can assume full control under your own hosting and management."
}, {
  title: "2. Permission & Access",
  content: "By signing the Agreement or paying any associated invoice, you grant Website Shop permission to access, manage, and use your brand’s assets, hosting, domain, and related materials as necessary to provide the Services. Upon termination, all access granted to Website Shop must be revoked, and passwords updated."
}, {
  title: "3. Best Practice",
  content: "Both Parties agree to comply with all applicable laws (copyright, privacy, data protection, etc.) and uphold confidentiality and professional standards."
}, {
  title: "4. Guarantees",
  content: "Website Shop will use its best professional efforts to design and manage your website effectively. However, no guarantee is made regarding specific results such as traffic, conversion rates, or revenue increases."
}, {
  title: "5. Revisions & Approvals",
  content: "You agree to review and approve drafts or designs within three (3) business days unless otherwise stated. Once approved, the Client assumes responsibility for any errors or issues in the content, copy, or design."
}, {
  title: "6. Rights of Use",
  content: "You may use all completed website files, graphics, and design assets delivered by Website Shop for your business purposes. Website Shop retains intellectual property rights to all underlying design concepts, frameworks, and source files."
}, {
  title: "7. Missed or Late Payments",
  content: "If payment is late, Website Shop may suspend services until full payment is received."
}, {
  title: "8. Additional Services & Fees",
  content: "Any services requested outside of the defined scope will be quoted and charged separately. Advertising costs (e.g. Google Ads, Meta campaigns) are paid directly to the respective platform."
}, {
  title: "9. Revisions and Content Changes",
  content: "Design Revisions: During the initial design phase, the Client is entitled to two (2) rounds of design revisions and one (1) round of content revisions (covering text, images, or video). Additional revisions requested beyond these limits will be quoted separately."
}, {
  title: "10. Timelines and Delays",
  content: "The Client must provide all necessary content, imagery, feedback, and approvals promptly. Website Shop is not responsible for delays caused by missing information or client inaction."
}, {
  title: "11. Contactable Hours",
  content: "Website Shop operates Monday to Friday, 9:00 AM – 5:00 PM (Sydney time). Emails and calls outside these hours may be answered at our discretion."
}, {
  title: "12. Amendments to Agreement",
  content: "Any new Agreement signed between the Parties will replace this one. The current Agreement remains binding until a replacement is signed."
}, {
  title: "13. Rate Changes",
  content: "Website Shop reserves the right to adjust pricing or services with 30 days’ written notice."
}, {
  title: "14. Termination",
  content: "(a) Client-initiated termination: The Client may not terminate this Agreement during the Initial Term except where Website Shop has materially breached this Agreement and failed to remedy that breach within fourteen (14) days of written notice.\n\n(b) Website Shop-initiated termination: Website Shop may terminate this Agreement if:\n– the Client fails to pay any invoice within fourteen (14) days of the due date;\n– the Client materially breaches this Agreement; or\n– circumstances outside Website Shop’s control prevent the provision of Services for more than forty-five (45) consecutive days.\n\n(c) Effect of termination: All outstanding invoices and any remaining payments for the Initial Term become immediately due and payable upon termination. No refunds or pro-rata credits will be issued.\n\n(d) Post-termination: Website Shop will provide all completed and fully paid deliverables in their current form. All access permissions must be revoked by the Client upon termination."
}];
export default function ClientAgreement() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="clientagreement" className="bg-base py-40 relative overflow-hidden">
      <div ref={ref} className={cn("container mx-auto px-6 max-w-3xl transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
        
        <div className="mb-16 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6 tracking-tight">Terms &amp; Conditions</h2>
          <p className="text-muted-foreground text-lg leading-relaxed max-w-2xl mx-auto">
            Please read these terms carefully before engaging with our services. 
            This document outlines the mutual agreement between <span className="text-primary font-medium">Website Shop</span> and the Client.
          </p>
        </div>

        <Separator className="mb-16 bg-border/50" />

        <div className="space-y-12">
          {clauses.map((clause, i) => <div key={i} data-index={i} className="group">
              <h3 className="text-2xl font-bold text-foreground mb-4 group-hover:text-primary transition-colors duration-300">
                {clause.title}
              </h3>
              <p className="text-muted-foreground leading-8 text-base md:text-lg whitespace-pre-line">
                {clause.content}
              </p>
            </div>)}
        </div>

        <Separator className="my-16 bg-border/50" />

        <div className="flex flex-col md:flex-row items-center justify-between gap-8 bg-secondary/30 p-8 rounded-2xl border border-border/50">
          <div className="text-center md:text-left">
            <h4 className="text-foreground font-bold text-xl mb-2">
              Have questions about these terms?
            </h4>
            <p className="text-muted-foreground">
              Our team is happy to clarify any details before you start.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4">
            <Button asChild variant="outline" className="border-primary/20 hover:bg-primary/10 hover:text-primary">
              <Link to="/ContactPage">
                <Mail className="mr-2 h-4 w-4" />
                Contact Support
              </Link>
            </Button>
            <Button asChild className="bg-primary text-primary-foreground hover:bg-primary">
              <Link to="/PackagesPage">
                View Packages
              </Link>
            </Button>
          </div>
        </div>

        <div className="mt-12 text-center">
          <p className="text-sm text-muted-foreground/60">Last updated: October 2025. These terms are subject to change without prior notice.</p>
        </div>
      </div>
    </section>;
}